import * as am4core from '@amcharts/amcharts4/core';
import { ForceDirectedSeries } from '@amcharts/amcharts4/plugins/forceDirected';

import { OwnershipGraphLinkResult } from '../../../../../../generated/model/ownershipGraphLinkResult';
import { OwnershipGraphResult } from '../../../../../../generated/model/ownershipGraphResult';
import {
  Icons,
  IconsType,
  OwnershipGraphNodeTypeEnum,
  OwnershipMappedGraphNode,
  OwnershipNodesColorEnum,
} from './OwnershipConnectionsChart.type';

export function generateNodes(
  data: OwnershipGraphResult,
  countryCodeToNameMap: Map<string, string>,
  onlyBeneficialOwnership: boolean,
  includeHistoricalOwnership: boolean,
) {
  const connectedNodes: OwnershipMappedGraphNode[] = [];
  const mappedLinks: any = {};
  connectedNodes.push({
    id: data.id,
    links: data.links,
    name: data.name,
    path: Icons.COMPANY,
    fixed: true,
    color: OwnershipNodesColorEnum.MAIN_COMPANY,
    countryLabel: data.country ? countryCodeToNameMap.get(data.country) : '',
    level: 1,
  });

  const links = onlyBeneficialOwnership ? data.beneficialLinks : data.links;
  const nodesMaxLevels = generateNodesMaxLevels(data.id!, links);
  if (links) {
    links
      .filter((link: any) => link.active || includeHistoricalOwnership)
      .forEach((link: any) => {
        const keyLink = data.id + '_' + link.id;
        if (typeof mappedLinks[keyLink] === 'undefined') {
          // save all links
          mappedLinks[keyLink] = link;
        }

        generateNodesInner(
          link,
          connectedNodes,
          mappedLinks,
          2,
          0,
          nodesMaxLevels,
          countryCodeToNameMap,
          includeHistoricalOwnership,
        );
      });
  }

  const holdings = data.holdingLinks;
  if (holdings && !onlyBeneficialOwnership) {
    holdings
      .filter((link: any) => link.active || includeHistoricalOwnership)
      .forEach((link: any) => {
        const keyLink = data.id + '_' + link.id;
        if (typeof mappedLinks[keyLink] === 'undefined') {
          // save all links
          mappedLinks[keyLink] = link;
        }

        generateNodesInner(
          link,
          connectedNodes,
          mappedLinks,
          0,
          0,
          nodesMaxLevels,
          countryCodeToNameMap,
          includeHistoricalOwnership,
        );
      });
  }

  return [connectedNodes, mappedLinks];
}

function generateNodesInner(
  data: OwnershipMappedGraphNode,
  connectedNodes: OwnershipMappedGraphNode[],
  mappedLinks: any,
  level: number,
  offset: number,
  nodesMaxLevels: Map<string, number>,
  countryCodeToNameMap: Map<string, string>,
  includeHistoricalOwnership: boolean,
) {
  // set unique connectedNodes
  const index = connectedNodes.findIndex((node) => node.id === data.id);
  if (index === -1) {
    connectedNodes.push(data);
  }

  //set icon
  let key: keyof IconsType = 'COMPANY';
  if (data.type) {
    key = data.type;
  }

  data.path = Icons[key];
  data.fixed = true;
  data.color = setNodesBackground(data);
  data.nodeValueText = data.nodeValue && data.nodeValue !== null ? `\n(${data.nodeValue}%)` : '';
  data.countryLabel =
    data.country && (!data.type || data.type === OwnershipGraphNodeTypeEnum.COMPANY)
      ? countryCodeToNameMap.get(data.country)
      : '';

  const dataId = data.id;
  const nodeMaxLevel = nodesMaxLevels.get(dataId!) || 0;

  data.level = Math.max(nodeMaxLevel, level + offset);
  const newOffset = nodeMaxLevel - level - offset > 0 ? nodeMaxLevel - level : offset;
  if (data.links) {
    data.links
      .filter((link: any) => link.active || includeHistoricalOwnership)
      .forEach((link: any) => {
        const keyLink = data.id + '_' + link.id;
        if (typeof mappedLinks[keyLink] === 'undefined') {
          // save all links
          mappedLinks[keyLink] = link;
        }

        generateNodesInner(
          link,
          connectedNodes,
          mappedLinks,
          level + 1,
          newOffset,
          nodesMaxLevels,
          countryCodeToNameMap,
          includeHistoricalOwnership,
        );
      });
  }
}

export function generateNodesMaxLevels(rootId: string, rootLinks?: OwnershipGraphLinkResult[]) {
  const nodesMaxLevels = new Map<string, number>();
  const processNodesLevels = (
    nodeId: string,
    level: number,
    links?: OwnershipGraphLinkResult[],
  ) => {
    const nodeLevel = nodesMaxLevels.get(nodeId);
    if (nodeLevel) {
      if (level > nodeLevel) {
        nodesMaxLevels.set(nodeId, level);
      }
    } else {
      nodesMaxLevels.set(nodeId, level);
    }

    if (links) {
      links.forEach((link) => processNodesLevels(link.id!, level + 1, link.links));
    }
  };

  processNodesLevels(rootId, 1, rootLinks);
  return nodesMaxLevels;
}

export function setNodeLinks(connectedNodes: OwnershipMappedGraphNode[], mappedLinks: any) {
  connectedNodes.forEach((node: OwnershipMappedGraphNode) => {
    const linkWith: string[] = [];
    const filteredKeys = Object.keys(mappedLinks).filter((key: string) => {
      return node.id ? key.indexOf(node.id) !== -1 : null;
    });

    filteredKeys.forEach((key: string) => {
      const link = key.replace(node.id + '_', '').replace('_' + node.id, '');
      if (linkWith.indexOf(link) === -1) {
        linkWith.push(link);
      }
    });
    node.linkWith = linkWith;
  });

  return connectedNodes;
}

export function setNodeCoordinates(connectedNodes: OwnershipMappedGraphNode[]) {
  const levels = connectedNodes
    .map((item: OwnershipMappedGraphNode) => item.level)
    .filter((value, index, self) => self.indexOf(value) === index);
  if (levels.length) {
    // const heightGap = 100 / levels[levels.length - 1];
    const heightGap = 16;
    const heightStart = levels.indexOf(0) !== -1 ? 75 : 85;

    levels.forEach((level: number) => {
      const filteredNodes = connectedNodes.filter(
        (node: OwnershipMappedGraphNode) => node.level === level,
      );

      const widthGap = filteredNodes.length > 5 ? 100 / filteredNodes.length : 20;
      let widthStart = 50 - ((filteredNodes.length - 1) * widthGap) / 2;
      widthStart += -level + (level % 2) * 4;

      filteredNodes.forEach((node: OwnershipMappedGraphNode) => {
        node.x = am4core.percent(level === 1 ? 50 : widthStart);
        node.y = am4core.percent(heightStart - heightGap * (level - 1));
        widthStart += widthGap;
      });
    });
  }

  return connectedNodes;
}

export const setNodesBackground = (element: OwnershipMappedGraphNode) => {
  if (!element.type) {
    return OwnershipNodesColorEnum.MAIN_COMPANY;
  }

  switch (element.type) {
    case OwnershipGraphNodeTypeEnum.COMPANY:
      return OwnershipNodesColorEnum.COMPANY;
    case OwnershipGraphNodeTypeEnum.PERSON:
      return OwnershipNodesColorEnum.PERSON;
  }
};

export function setExportingData(series: ForceDirectedSeries) {
  series.exporting.adapter.add('data', (data) => {
    data.data = [];
    data.data = series.data;
    return data;
  });
}
